import { useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowRight, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/Logo";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";

const Shell = ({ children, title, subtitle }) => (
  <div className="grid min-h-screen lg:grid-cols-2" data-testid="auth-page">
    <div className="noise-dark relative hidden flex-col justify-between bg-navydeep p-12 text-white lg:flex">
      <Link to="/" className="flex items-center gap-3" data-testid="auth-home-link">
        <LogoMark className="h-10 w-10" />
        <span className="font-serif text-xl font-semibold">MITIVISE</span>
      </Link>
      <div>
        <h2 className="font-serif text-4xl font-semibold leading-tight">
          Your business, <span className="italic text-gold">ready for anything.</span>
        </h2>
        <p className="mt-4 max-w-sm text-white/60">
          Risks, audits, SOPs, documents, and accountability — organized in one secure workspace by TSM Group CLE.
        </p>
      </div>
      <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/40">Trust · Systems · Management</p>
    </div>
    <div className="flex items-center justify-center bg-paper p-6">
      <div className="w-full max-w-md">
        <h1 className="font-serif text-4xl font-semibold text-navy">{title}</h1>
        <p className="mt-2 text-sm text-ink/60">{subtitle}</p>
        <div className="mt-8">{children}</div>
      </div>
    </div>
  </div>
);

const field = "w-full rounded-none border-0 border-b-2 border-navy/15 bg-transparent px-0 py-2.5 text-base text-navy shadow-none transition-colors placeholder:text-navy/35 focus:border-gold focus:outline-none";
const label = "mb-1.5 block font-mono text-[11px] font-semibold uppercase tracking-[0.16em] text-navy/60";

export const Login = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/auth/login", form);
      setUser(data);
      navigate(data.role === "admin" ? "/admin" : "/app", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Welcome back." subtitle="Sign in to your MITIVISE account.">
      <form onSubmit={submit} data-testid="login-form">
        <div className="space-y-6">
          <div>
            <label className={label} htmlFor="login-email">Email</label>
            <input id="login-email" type="email" required className={field} value={form.email}
                   onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="login-email" placeholder="you@company.com" />
          </div>
          <div>
            <label className={label} htmlFor="login-password">Password</label>
            <input id="login-password" type="password" required className={field} value={form.password}
                   onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="login-password" placeholder="••••••••" />
          </div>
          {error && <p className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="login-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="login-submit"
                  className="btn-gold w-full disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Sign In
          </button>
          <div className="flex justify-between text-sm">
            <Link to="/forgot-password" className="text-navy/60 hover:text-golddark" data-testid="login-forgot-link">Forgot password?</Link>
            <Link to="/register" className="font-semibold text-navy hover:text-golddark" data-testid="login-register-link">Create an account</Link>
          </div>
          <p className="rounded-sm border border-gold/40 bg-gold/10 px-4 py-3 text-xs leading-relaxed text-navy/70">
            <span className="font-semibold">Demo account:</span> demo@tsmgroupcle.com · DemoPass2026! — a fictional Ohio plumbing business with sample data.
          </p>
        </div>
      </form>
    </Shell>
  );
};

export const Register = () => {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", business_name: "", email: "", password: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/auth/register", form);
      setUser(data);
      navigate("/app/onboarding", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Create your account." subtitle="Start organizing your business compliance in minutes.">
      <form onSubmit={submit} data-testid="register-form">
        <div className="space-y-6">
          <div>
            <label className={label} htmlFor="reg-name">Your Name</label>
            <input id="reg-name" required className={field} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="register-input-name" placeholder="Jordan Reyes" />
          </div>
          <div>
            <label className={label} htmlFor="reg-business">Business Name</label>
            <input id="reg-business" required className={field} value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} data-testid="register-input-business" placeholder="Your Business LLC" />
          </div>
          <div>
            <label className={label} htmlFor="reg-email">Email</label>
            <input id="reg-email" type="email" required className={field} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="register-input-email" placeholder="you@company.com" />
          </div>
          <div>
            <label className={label} htmlFor="reg-password">Password</label>
            <input id="reg-password" type="password" required minLength={8} className={field} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="register-input-password" placeholder="At least 8 characters" />
          </div>
          {error && <p className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="register-submit" className="btn-gold w-full disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Create Account
          </button>
          <p className="text-center text-sm text-navy/60">
            Already registered? <Link to="/login" className="font-semibold text-navy hover:text-golddark" data-testid="register-login-link">Sign in</Link>
          </p>
        </div>
      </form>
    </Shell>
  );
};

export const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/api/auth/forgot-password", { email });
    } finally {
      setDone(true);
      setBusy(false);
    }
  };

  return (
    <Shell title="Reset your password." subtitle="We'll email you a secure reset link.">
      {done ? (
        <div className="rounded-sm border border-gold/40 bg-gold/10 p-5 text-sm text-navy/80" data-testid="forgot-success">
          If an account exists for that email, a reset link has been sent. The link expires in one hour.
        </div>
      ) : (
        <form onSubmit={submit} data-testid="forgot-form">
          <div className="space-y-6">
            <div>
              <label className={label} htmlFor="forgot-email">Email</label>
              <input id="forgot-email" type="email" required className={field} value={email} onChange={(e) => setEmail(e.target.value)} data-testid="forgot-email" placeholder="you@company.com" />
            </div>
            <button type="submit" disabled={busy} data-testid="forgot-submit" className="btn-gold w-full disabled:opacity-60">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              Send Reset Link
            </button>
            <p className="text-center text-sm"><Link to="/login" className="font-semibold text-navy hover:text-golddark" data-testid="forgot-login-link">Back to sign in</Link></p>
          </div>
        </form>
      )}
    </Shell>
  );
};

export const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [form, setForm] = useState({ password: "", confirm: "" });
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) {
      setError("Passwords do not match.");
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.post("/api/auth/reset-password", { token: params.get("token") || "", password: form.password });
      navigate("/login", { replace: true });
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Shell title="Choose a new password." subtitle="Make it at least 8 characters.">
      <form onSubmit={submit} data-testid="reset-form">
        <div className="space-y-6">
          <div>
            <label className={label} htmlFor="reset-password">New Password</label>
            <input id="reset-password" type="password" required minLength={8} className={field} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="reset-input-password" />
          </div>
          <div>
            <label className={label} htmlFor="reset-confirm">Confirm Password</label>
            <input id="reset-confirm" type="password" required minLength={8} className={field} value={form.confirm} onChange={(e) => setForm({ ...form, confirm: e.target.value })} data-testid="reset-input-confirm" />
          </div>
          {error && <p className="rounded-sm border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" role="alert" data-testid="reset-error">{error}</p>}
          <button type="submit" disabled={busy} data-testid="reset-submit" className="btn-gold w-full disabled:opacity-60">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
            Update Password
          </button>
        </div>
      </form>
    </Shell>
  );
};

export const RequireAuth = ({ children, admin = false }) => {
  const { user } = useAuth();
  const location = useLocation();

  if (user === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-paper" data-testid="auth-loading">
        <Loader2 className="h-8 w-8 animate-spin text-gold" />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  if (admin && user.role !== "admin") return <Navigate to="/app" replace />;
  if (!admin && user.role === "admin") return <Navigate to="/admin" replace />;
  return children;
};
